import React, { ReactNode, useRef } from "react";
import styled from "@emotion/styled";
import isPropValid from "@emotion/is-prop-valid";
import { useScreenSizes } from "../../hooks/useSizes";
import { useStyles } from "../../hooks/useStyles";
import { IFontWeightName } from "../../theme/fonts";
import { IStyledTheme } from "../../theme/interfaces";
import { fontWeightFromName } from "../../utils/dimensions";
import { weightToFont } from "../../utils/helpers";
import { makeConditionalStyles } from "../../utils/styles";
import { useLineHeight } from "../../hooks/useLineHeight";

import { dotGet } from "../../utils/utils";

const createStyles = ({
  theme,
  colors,
  stylesProps,
}: IStyledTheme<StylesProps>) => {
  const {
    fontWeight,
    fontSize,
    adjustedLineHeight,
    transform: textTransform,
    fontStyle,
    color,
    role,
    align,
    isDesktop,
    decoration,
  } = stylesProps;

  let fontColor = color;

  if (!color) {
    const colorPath = theme.fonts.roles[role].colors.default;
    fontColor = dotGet(colors, colorPath)?.color;
  }

  const styles = {
    padding: 0,
    margin: 0,
    fontFamily: weightToFont(fontWeight),
    fontSize,
    fontWeight: fontWeightFromName(fontWeight),
    lineHeight: adjustedLineHeight,
    textTransform,
    fontStyle,
    color: fontColor,
    textAlign: align,
    ...makeConditionalStyles(
      { textDecorationLine: decoration, textDecorationColor: fontColor },
      !isDesktop
    ),
  };

  return styles;
};

const HEADING_VARIANTS = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "p",
  "span",
] as const;
const OTHER_VARIANTS = ["p", "span"] as const;

const Typography = ({
  children,
  className,
  variant = "p",
  component,
  style,
  size = "default",
  fontWeight = "regular",
  onClick,
  decoration = "none",
  transform = "none",
  fontStyle = "normal",
  role = "primary",
  color,
  lineHeight,
  align = "left",
  testId,
}: TypographyProps) => {
  const screenSizes = useScreenSizes();

  const ref = useRef(null);
  const { fontSize, lineHeight: adjustedLineHeight } = useLineHeight({
    variant,
    size,
    lineHeight,
  });

  const alias = component || variant;

  const styles = useStyles(createStyles, {
    fontWeight,
    fontSize,
    adjustedLineHeight,
    transform,
    fontStyle,
    color,
    role,
    align,
    isDesktop: screenSizes.desktop,
    decoration,
  });

  const overrideStyle = style;

  const typographyStyles = {
    ...styles,
    lineHeight: adjustedLineHeight ? `${adjustedLineHeight}px` : undefined,
    ...overrideStyle,
  };

  const StyledTypography = styled(alias, {
    shouldForwardProp: (prop) => isPropValid(prop),
  })`
    ${typographyStyles}
  `;

  return (
    <StyledTypography
      ref={ref}
      className={className ?? ""}
      onClick={onClick}
      data-testid={testId}
    >
      {children}
    </StyledTypography>
  );
};

interface CommonProps {
  children: React.ReactNode;
}

type HeadingVariants = (typeof HEADING_VARIANTS)[number];
type OtherVariants = (typeof OTHER_VARIANTS)[number];
type TypographyVariants = HeadingVariants | OtherVariants;

type HeadingProps = {
  variant: HeadingVariants;
} & React.HTMLProps<HTMLHeadingElement>;

type ParagraphProps = React.HTMLProps<HTMLParagraphElement>;
type SpanProps = React.HTMLProps<HTMLSpanElement>;

type ConditionalProps =
  | ({
      variant: HeadingVariants;
    } & HeadingProps)
  | ({
      variant: "p";
    } & ParagraphProps)
  | ({
      variant: "span";
    } & SpanProps);

type TypographyElementProps = CommonProps & ConditionalProps;

type StylesProps = {
  fontSize: number;
  adjustedLineHeight: number;
  isDesktop: boolean;
} & Required<
  Pick<
    Props,
    "fontWeight" | "transform" | "fontStyle" | "role" | "align" | "decoration"
  >
> &
  Partial<Pick<Props, "color">>;

type Props = {
  variant?: TypographyVariants;
  component?: TypographyVariants;
  size?: "default" | "small" | "large" | "tiny" | "extra_small" | number;
  decoration?: "none" | "underline" | "line-through" | "underline line-through";
  transform?: "capitalize" | "uppercase" | "lowercase" | "none";
  fontStyle?: "normal" | "italic";
  role?: "primary" | "secondary" | "tertiary" | "inverted" | "base";
  color?: string;
  lineHeight?: number;
  fontWeight?: IFontWeightName;
  numberOfLines?: number;
  align?: "center" | "left" | "right";
  children?: ReactNode;
  onClick?: () => void;
  testId?: string;
};

type TypographyProps = Props & Omit<TypographyElementProps, "size" | "variant">;

export { Typography };
export type { TypographyProps };
